import isWall from 'components/configurator/propertiesBar/services/isWall';
import calculateGlassLineLength from 'components/configurator/services/calculateGlassLineLength';
import getDoorPosition from 'components/configurator/services/getDoorPosition';
import getDoorsFromWallParts from 'components/configurator/services/getDoorsFromWallParts';
import handleFirstAndLastDoorSettings from 'components/configurator/services/handleFirstAndLastDoorSettings';
import IGlassArea from 'components/interfaces/IGlassArea';
import IGlassAreaMetaData from 'components/interfaces/IGlassAreaMetaData';
import IGlassWallDoor from 'components/interfaces/IGlassWallDoor';
import IGlassWallGlass from 'components/interfaces/IGlassWallGlass';
import IWall from 'components/interfaces/IWall';
import store from 'store/store';

import getDefaultGlassWallGlass from './getDefaultGlassWallGlass';
import getGlassWidths from './getGlassWidths';
import getIndividualGlassTypes from './getIndividualGlassTypes';
import { isWoodyTypeWall } from '../../../types/glazingWallTypesCheck';

const getGlassIds = (
  glassArea: IGlassWallGlass,
  glassAreas: IGlassArea[],
  glassIndex: number
) =>
  glassArea.glassWidths.map((width, index) => {
    if (glassAreas[glassIndex].name === '') {
      return (index + 1).toString();
    }

    return `${glassAreas[glassIndex].name} / ${index + 1}`;
  });

const isGlazyDouble = (wall: IWall) => {
  return wall.glazingWallType === 'glazydouble';
};

const getFirstGlass = (
  door: IGlassWallDoor,
  insertPosition: number,
  wall: IWall,
  glassAreas: IGlassArea[],
  glassAreasMetaData: IGlassAreaMetaData[]
): IGlassWallGlass => {
  const glassAreaIndex = 0;
  const length = getDoorPosition(door, wall).glass.left - insertPosition;
  const glassArea = getDefaultGlassWallGlass(glassAreaIndex + 1);
  glassArea.insertPosition = insertPosition;
  glassArea.totalAreaLength = length;
  glassArea.areaName = glassAreas[glassAreaIndex].name;
  glassArea.glassWidths = getGlassWidths(
    length,
    wall,
    glassAreas[0],
    glassAreasMetaData[0]
  );
  glassArea.glassIds = getGlassIds(glassArea, glassAreas, glassAreaIndex);

  if (isGlazyDouble(wall)) {
    glassArea.typeName = 'alu2kglazy';
    glassArea.individualGlassTypes = getIndividualGlassTypes(
      wall,
      glassArea.glassIds
    );
  }

  return glassArea;
};

const getLastGlass = (
  doors: IGlassWallDoor[],
  wall: IWall,
  glassEndPoint: number,
  glassAreas: IGlassArea[],
  glassAreasMetaData: IGlassAreaMetaData[]
): IGlassWallGlass => {
  const glassAreaIndex = doors[0].isFirstDoor ? doors.length - 1 : doors.length;
  const door = doors[doors.length - 1];
  const doorRightEdge = getDoorPosition(door, wall).glass.right;
  const glassLength = glassEndPoint - doorRightEdge;
  const glassArea = getDefaultGlassWallGlass(glassAreaIndex + 1);
  glassArea.insertPosition = doorRightEdge;
  glassArea.totalAreaLength = glassLength;
  glassArea.areaName = glassAreas[glassAreaIndex].name;

  glassArea.glassWidths = getGlassWidths(
    glassLength,
    wall,
    glassAreas[glassAreaIndex],
    glassAreasMetaData[glassAreaIndex]
  );

  glassArea.glassIds = getGlassIds(glassArea, glassAreas, glassAreaIndex);

  if (isGlazyDouble(wall)) {
    glassArea.typeName = 'alu2kglazy';
    glassArea.individualGlassTypes = getIndividualGlassTypes(
      wall,
      glassArea.glassIds
    );
  }

  return glassArea;
};

const getDoorsWithMiddleGlasses = (
  doors: IGlassWallDoor[],
  wall: IWall,
  glassAreas: IGlassArea[],
  glassAreasMetaData: IGlassAreaMetaData[]
): (IGlassWallDoor | IGlassWallGlass)[] => {
  if (doors.length === 1) {
    return doors;
  }

  return doors.reduce(
    (result: (IGlassWallDoor | IGlassWallGlass)[], door, index) => {
      if (index === 0) {
        return [door];
      }

      const glassAreaIndex = doors[0].isFirstDoor ? index - 1 : index;

      const glassArea = getDefaultGlassWallGlass(glassAreaIndex + 1);

      const leftDoor: IGlassWallDoor = result[
        result.length - 1
      ] as IGlassWallDoor;

      glassArea.insertPosition = getDoorPosition(leftDoor, wall).glass.right;

      const doorStartPosition = getDoorPosition(door, wall).glass.left;

      const glassAreaLength = doorStartPosition - glassArea.insertPosition;

      glassArea.totalAreaLength = glassAreaLength;

      glassArea.glassWidths = getGlassWidths(
        glassAreaLength,
        wall,
        glassAreas[glassAreaIndex],
        glassAreasMetaData[glassAreaIndex]
      );

      glassArea.areaName = glassAreas[glassAreaIndex].name;

      glassArea.glassIds = getGlassIds(glassArea, glassAreas, glassAreaIndex);

      if (isGlazyDouble(wall)) {
        glassArea.typeName = 'alu2kglazy';
        glassArea.individualGlassTypes = getIndividualGlassTypes(
          wall,
          glassArea.glassIds
        );
      }

      return [...result, glassArea, door];
    },
    []
  );
};

const getGlassWallPartsWithoutDoors = (
  length: number,
  insertPosition: number,
  wall: IWall,
  glassAreas: IGlassArea[],
  glassAreasMetaData: IGlassAreaMetaData[]
) => {
  const glassAreaIndex = 0;
  const glassArea = getDefaultGlassWallGlass(glassAreaIndex + 1);
  glassArea.insertPosition = insertPosition;
  glassArea.totalAreaLength = length;
  glassArea.areaName = glassAreas[glassAreaIndex].name;

  glassArea.glassWidths = getGlassWidths(
    length,
    wall,
    glassAreas[0],
    glassAreasMetaData[0]
  );

  glassArea.glassIds = getGlassIds(glassArea, glassAreas, glassAreaIndex);

  if (isGlazyDouble(wall)) {
    glassArea.typeName = 'alu2kglazy';
    glassArea.individualGlassTypes = getIndividualGlassTypes(
      wall,
      glassArea.glassIds
    );
  }

  return [glassArea];
};

const getGlassWallPartsWithOnlyDoor = (
  door: IGlassWallDoor,
  totalGlassLength: number,
  glassAreas: IGlassArea[]
) => {
  const glassAreaIndex = 0;
  const glassArea = getDefaultGlassWallGlass(glassAreaIndex + 1);
  glassArea.insertPosition = (totalGlassLength / 4) * 3;
  glassArea.totalAreaLength = 0;
  glassArea.areaName = glassAreas[glassAreaIndex].name;
  glassArea.glassWidths = [0];
  glassArea.glassIds = [`${glassAreas[0].name} / 1`];
  return [door, glassArea];
};

const placeGlasses = (
  wall: IWall,
  glassAreas: IGlassArea[]
): (IGlassWallDoor | IGlassWallGlass)[] => {
  const parts = getDoorsFromWallParts(wall);

  const { glassEndPoint, glassStartPoint, totalGlassLength } =
    calculateGlassLineLength(wall);

  const state = store.getState();
  const wallMetaData = state.selectedWall.metaData;
  const glassAreasMetaData =
    wallMetaData === null ? [] : wallMetaData.glassAreas;

  if (parts.length === 1 && wall.onlyDoor) {
    return getGlassWallPartsWithOnlyDoor(
      parts[0],
      totalGlassLength,
      glassAreas
    );
  }

  if (parts.length === 0) {
    return getGlassWallPartsWithoutDoors(
      totalGlassLength,
      glassStartPoint,
      wall,
      glassAreas,
      glassAreasMetaData
    );
  }

  if (isWoodyTypeWall(wall)) {
    parts.forEach((d) => {
      d.woodyDoorLeafColor = wall.woodyDoorLeafColor;
      d.woodyDoorFrameColor = wall.woodyDoorFrameColor;
    });
  }

  const doors = handleFirstAndLastDoorSettings(
    [...parts].map((door) => ({
      ...door,
      totalAreaLength: getDoorPosition(door, wall).totalAreaLength,
    })),
    wall
  );

  let resultParts = getDoorsWithMiddleGlasses(
    doors,
    wall,
    glassAreas,
    glassAreasMetaData
  );

  if (!doors[0].isFirstDoor) {
    resultParts = [
      getFirstGlass(
        doors[0],
        glassStartPoint,
        wall,
        glassAreas,
        glassAreasMetaData
      ),
      ...resultParts,
    ];
  }

  if (!doors[doors.length - 1].isLastDoor) {
    resultParts = [
      ...resultParts,
      getLastGlass(doors, wall, glassEndPoint, glassAreas, glassAreasMetaData),
    ];
  }

  return resultParts;
};

export default placeGlasses;
